import { useState, useEffect } from 'react';

function daysInMonth(date: Date): number {
    const month = date.getMonth();
    if (month === 1) {
        const check = new Date(date.getFullYear(), 2, 0);
        return check.getDate(); // will be 29 if a leap year
    }
    if (month === 3 || month === 5 || month === 8 || month === 10) {
        return 30;
    }
    return 31;
}

function buildDateMatrix(date: Date): any[] {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const dim = daysInMonth(date);
    const days: any[] = [];

    for (let i = 0; i < startOfMonth.getDay(); i++) {
        days.push(0);
    }
    for (let i = 1; i <= dim; i++) {
        days.push(i);
    }
    const matrix: any[] = [];

    for (let i = 0; i < days.length + 1; i += 7) {
        const temp = days.slice(i, i + 7);
        matrix.push(temp);
    }
    /// add trailing 0's
    return matrix;
}

const useDate = (initDate: Date | null, injectedDate?: Date | null, injectedSetDate?: (date: Date | null) => void) => {
    const [internalDate, setInternalDate] = useState(initDate || new Date());
    const [matrix, setMatrix] = useState<any[]>([]);

    const [date, setDate] = (injectedDate && injectedSetDate) ? [injectedDate, injectedSetDate] : [internalDate, setInternalDate];

    useEffect(() => {
        if (date) {
            setMatrix(buildDateMatrix(date));
        }
    }, [date]);

    const today = () => {
        const now = new Date(Date.now());
        setDate(new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
        ));
    };

    const now = () => {
        const now = new Date(Date.now());
        setDate(new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            now.getHours(),
            now.getMinutes(),
            now.getSeconds()
        ));
    }

    const incMonth = () => {
        setDate(new Date(
            date.getMonth() + 1 > 11 ? date.getFullYear() + 1 : date.getFullYear(),
            date.getMonth() + 1 > 11 ? 0 : date.getMonth() + 1,
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
        ));
    };

    const decMonth = () => {
        setDate(new Date(
            date.getMonth() - 1 < 0 ? date.getFullYear() - 1 : date.getFullYear(),
            date.getMonth() - 1 < 0 ? 11 : date.getMonth() - 1,
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
        ));
    }

    const decYear = () => {
        setDate(new Date(
            date.getFullYear() - 1,
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
        ));
    };

    const incYear = () => {
        setDate(new Date(
            date.getFullYear() + 1,
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
        ));
    };

    const incHours = () => {
        setDate(new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours() + 1 > 23 ? 0 : date.getHours() + 1,
            date.getMinutes(),
            date.getSeconds()
        ));
    };

    const incMins = () => {
        setDate(new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes() + 1 > 59 ? 0 : date.getMinutes() + 1,
            date.getSeconds()
        ));
    };

    const decHours = () => {
        setDate(new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours() - 1 < 0 ? 23 : date.getHours() - 1,
            date.getMinutes(),
            date.getSeconds()
        ));
    };

    const decMins = () => {
        setDate(new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes() - 1 < 0 ? 59 : date.getMinutes() - 1,
            date.getSeconds()
        ));
    };

    const onHoursChange = (hours) => {
        const tempHours = hours >= 0 && hours <= 24 ? hours : 23;
        setDate(new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            tempHours,
            date.getMinutes(),
            date.getSeconds()
        ));
    }

    const onMinutesChange = (minutes) => {
        const tempMinutes = minutes >= 0 && minutes <= 60 ? minutes : 59;
        setDate(new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            tempMinutes,
            date.getSeconds()
        ));
    }

    return { date, matrix, setDate: (d) => setDate(d), decMonth, incMonth, decYear, incYear, incHours, incMins, decHours, decMins, today, now, onHoursChange, onMinutesChange };
}

export default useDate;
